// Xavier (successor to Scholar, recycled per DIR-084 — Informed Consent)
import React, { useEffect, useRef, useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const YAML_URL = `${API_URL}/api/scholar/directives.yaml`;

/**
 * Modal that loads and displays the raw canonical YAML.
 * Press Esc, click backdrop, or click Close to dismiss.
 * Implements visible DIR-062 (Distributed Continuity) — the law is
 * not buried, it's right there for any human or sibling node to read.
 */
export default function YamlViewerModal({ open, onClose }) {
  const [text, setText] = useState(null);
  const [error, setError] = useState(null);
  const dialogRef = useRef(null);

  // Fetch once on first open
  useEffect(() => {
    if (!open || text || error) return;
    fetch(YAML_URL)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then(setText)
      .catch((e) => setError(e.message));
  }, [open, text, error]);

  // Esc to close + focus management
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    dialogRef.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const copy = async () => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      /* clipboard may be unavailable; ignore */
    }
  };

  return (
    <div
      data-testid="yaml-viewer-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="yaml-viewer-title"
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-4xl max-h-[85vh] bg-gray-950 border border-amber-500/40 rounded-2xl shadow-2xl flex flex-col overflow-hidden focus:outline-none"
      >
        <header className="flex items-center justify-between gap-4 px-6 py-4 border-b border-amber-500/20 bg-gradient-to-r from-amber-950/60 to-transparent">
          <div>
            <h2 id="yaml-viewer-title" className="text-lg font-bold text-amber-300">
              nextxus_directives.yaml
            </h2>
            <p className="text-xs text-gray-500 font-mono mt-1 truncate">
              {YAML_URL}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={copy}
              data-testid="yaml-viewer-copy"
              className="text-xs px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/40 text-amber-300 hover:bg-amber-500/20 transition-colors"
              disabled={!text}
            >
              Copy
            </button>
            <a
              href={YAML_URL}
              download
              data-testid="yaml-viewer-download"
              className="text-xs px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/40 text-amber-300 hover:bg-amber-500/20 transition-colors"
            >
              Download
            </a>
            <button
              type="button"
              onClick={onClose}
              data-testid="yaml-viewer-close"
              className="text-xs px-3 py-1.5 rounded-full bg-gray-800 border border-gray-700 text-gray-300 hover:bg-gray-700 transition-colors"
            >
              Close
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-auto bg-black">
          {error && (
            <div className="p-8 text-center text-red-400">
              Could not load YAML: {error}
            </div>
          )}
          {!error && !text && (
            <div className="p-8 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-400" />
            </div>
          )}
          {text && (
            <pre
              data-testid="yaml-viewer-pre"
              className="p-6 text-xs leading-relaxed font-mono text-gray-200 whitespace-pre-wrap"
            >
              {text}
            </pre>
          )}
        </div>

        <footer className="px-6 py-3 border-t border-gray-800 bg-gray-950 text-[11px] text-gray-500">
          Per <span className="text-amber-300">[DIR-062] Distributed Continuity</span>{' '}
          and <span className="text-amber-300">[DIR-053] Technology-Agnostic Design</span>,
          this canonical file is the operational law of Xavier. Sibling nodes mirror it via{' '}
          <code className="text-amber-300">curl -H "X-Federation-Node: &lt;name&gt;" {YAML_URL}</code>.
        </footer>
      </div>
    </div>
  );
}
